import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Sparkle, ArrowsClockwise, Trash, Copy, DownloadSimple,
  SignIn, FileText, Eye, Wrench,
} from '@phosphor-icons/react';

import { safeCopy } from '../lib/safeCopy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Pitch-deck demo brand control: one-click seed, refresh, reset.
 *
 *  Once seeded, surfaces the four follow-on actions an admin actually
 *  needs:
 *   1) Download all reports as ZIP (drop into a pitch deck)
 *   2) Open the demo brand portal (auto-signs in as the demo user)
 *   3) Direct deep-links to each seeded report PDF
 *   4) Copy credentials
 */
export default function DemoBrandPanel() {
  const [status, setStatus] = useState(null);
  const [formulations, setFormulations] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/demo/status`, { withCredentials: true });
      setStatus(r.data);
      if (r.data?.seeded) {
        const f = await axios.get(`${API_URL}/api/admin/demo/formulations`, { withCredentials: true });
        setFormulations(f.data?.items || []);
      } else {
        setFormulations([]);
      }
    } catch (e) { /* ignored */ }
  };
  useEffect(() => { refresh(); }, []);

  const seed = async (refreshData = false) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/demo/seed`, {}, {
        params: { refresh: refreshData }, withCredentials: true,
      });
      toast.success(r.data.status === 'already_seeded'
        ? 'Demo brand already present'
        : 'Demo brand seeded ✓');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    }
    setBusy(false);
  };

  const reset = async () => {
    if (!window.confirm('Wipe every demo record (formulations, Hale SKUs, portfolio, marketplace, and the demo user)?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/demo/reset`, {}, { withCredentials: true });
      const total = Object.values(r.data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Removed ${total} demo records`);
      refresh();
    } catch (e) {
      toast.error('Reset failed');
    }
    setBusy(false);
  };

  const copyCreds = async () => {
    if (!status?.demo_user) return;
    const text = `Email: ${status.demo_user.email}\nPassword: ${status.demo_user.password}`;
    const ok = await safeCopy(text);
    if (ok) toast.success('Credentials copied');
    else toast.info('Copy blocked by browser — credentials shown in prompt');
  };

  const downloadZip = () => {
    // Programmatic same-tab anchor click: the browser intercepts the
    // attachment response at the header stage and triggers a native
    // download, avoiding the mobile Chrome "blank new tab" gotcha that
    // window.location.href would produce inside this iframe sandbox.
    const a = document.createElement('a');
    a.href = `${API_URL}/api/admin/demo/download-zip`;
    a.download = 'demo_brand_reports.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Bundling demo reports · check your Downloads folder');
  };

  /** Sign out of the admin session and log in as the demo brand user.
   *  Lands on /dashboard so the admin sees exactly what a brand sees. */
  const openAsDemoBrand = async () => {
    if (!status?.demo_user) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      await axios.post(`${API_URL}/api/auth/login`, {
        email: status.demo_user.email,
        password: status.demo_user.password,
      }, { withCredentials: true });
      toast.success('Signed in as Atelier Verdé · redirecting…');
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error('Could not switch to demo user');
      setBusy(false);
    }
  };

  const seeded = status?.seeded;
  const c = status?.counts || {};

  return (
    <div className="card-flat p-4 mb-6" data-testid="demo-brand-panel">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded bg-[#FF4E6A] flex items-center justify-center flex-shrink-0">
          <Sparkle size={18} className="text-white" weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-[#111827]">Demo Brand · Pitch Deck Mode</h3>
          <p className="text-[11px] text-[#4B5563] leading-relaxed mt-0.5">
            One-click seed of "Atelier Verdé" — 4 formulations (Risk Screen / TRA-QRA / Full / concern),
            3 Hale SKUs (certified / concern / in-review), a quoted portfolio request, and an active
            marketplace subscription. All records carry <code className="bg-gray-100 px-1 rounded">is_demo=true</code>.
          </p>

          {seeded && status?.demo_user && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {/* Credentials block */}
              <div className="bg-[#F9FAFB] border border-gray-200 rounded p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1.5">Demo credentials</p>
                <div className="text-[10px] font-mono text-[#111827] space-y-0.5">
                  <div>{status.demo_user.email}</div>
                  <div>{status.demo_user.password}</div>
                </div>
                <button onClick={copyCreds} className="text-[10px] underline text-[#FF4E6A] mt-1.5" data-testid="demo-copy-creds">
                  <Copy size={11} className="inline mr-1" /> copy
                </button>
              </div>

              {/* Counts block */}
              <div className="bg-[#F9FAFB] border border-gray-200 rounded p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1.5">Seeded data</p>
                <div className="text-[11px] text-[#111827] space-y-0.5">
                  <div><span className="font-mono">{c.formulations}</span> formulations · <span className="font-mono">{c.hale_skus}</span> Hale SKUs</div>
                  <div><span className="font-mono">{c.portfolio_requests}</span> portfolio · <span className="font-mono">{c.marketplace_subscriptions}</span> subscription</div>
                </div>
              </div>
            </div>
          )}

          {/* Primary action row */}
          <div className="flex gap-2 flex-wrap mt-3">
            {!seeded ? (
              <Button onClick={() => seed(false)} disabled={busy}
                className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white"
                data-testid="demo-seed">
                <Sparkle size={13} weight="fill" className="mr-1" />
                {busy ? 'Seeding…' : 'Seed demo brand'}
              </Button>
            ) : (
              <>
                <Button onClick={downloadZip} disabled={busy}
                  className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white"
                  data-testid="demo-download-zip">
                  <DownloadSimple size={13} weight="bold" className="mr-1" />
                  Download reports (ZIP)
                </Button>
                <Button onClick={openAsDemoBrand} disabled={busy}
                  className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white"
                  data-testid="demo-open-portal">
                  <SignIn size={13} weight="bold" className="mr-1" />
                  Open demo brand portal
                </Button>
                <Button onClick={() => seed(true)} disabled={busy} variant="outline"
                  className="h-8 text-xs rounded" data-testid="demo-refresh">
                  <ArrowsClockwise size={13} weight="bold" className="mr-1" />
                  {busy ? 'Refreshing…' : 'Refresh data'}
                </Button>
                <Button onClick={reset} disabled={busy} variant="outline"
                  className="h-8 text-xs rounded text-[#9B1C1C] border-[#F8B4B4] hover:bg-[#FEE2E2]"
                  data-testid="demo-reset">
                  <Trash size={13} weight="bold" className="mr-1" /> Reset
                </Button>
              </>
            )}
          </div>

          {/* Per-formulation download links */}
          {seeded && formulations.length > 0 && (
            <div className="mt-3 border-t border-gray-100 pt-3" data-testid="demo-formulations">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">Individual reports</p>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {formulations.map(f => (
                  <div key={f.id} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-[#F9FAFB] border border-gray-200 rounded text-[11px]">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#111827] truncate">{f.product_name}</p>
                      <p className="text-[9px] text-[#4B5563] uppercase tracking-wider">
                        {f.assessment_mode} · {f.overall_status}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Link to={`/assessment/${f.id}`} title="View in assessor"
                        className="p-1 rounded hover:bg-white border border-transparent hover:border-gray-200"
                        data-testid={`demo-view-${f.id}`}>
                        <Eye size={13} className="text-[#4B5563]" />
                      </Link>
                      <a href={`${API_URL}/api/formulations/${f.id}/report`} target="_blank" rel="noreferrer"
                        title="Download PDF"
                        className="p-1 rounded hover:bg-white border border-transparent hover:border-gray-200"
                        data-testid={`demo-pdf-${f.id}`}>
                        <FileText size={13} className="text-[#FF4E6A]" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pitch deck downloads — always visible (independent of demo seed).
              Each row triggers a same-tab attachment download so mobile
              Chrome doesn't leave the user staring at a blank popup. */}
          <div className="mt-3 border-t border-gray-100 pt-3" data-testid="pitch-decks">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563]">Pitch decks (PPTX)</p>
              <div className="flex items-baseline gap-3">
                <RebuildPitchPackButton onDone={() => { /* nothing local to refresh */ }} />
                <button onClick={() => {
                  const a = document.createElement('a');
                  a.href = `${API_URL}/api/pitch/download/Hale_ToxIntel_Pitch_Pack.zip`;
                  a.download = 'Hale_ToxIntel_Pitch_Pack.zip';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  toast.success('Download started · check your Downloads folder');
                }}
                   className="text-[10px] font-bold uppercase tracking-wider text-[#E63946] hover:text-[#C9192C]"
                   data-testid="download-pitch-zip">
                  ↓ All decks · ZIP
                </button>
              </div>
            </div>
            <DeckRow file="Hale_ToxIntel_Hale_Standards_Deck_v2.pptx"
                     name="HALE Standards Deck (v2)"
                     meta="12 slides · Slide 8 enterprise fees populated"
                     testid="download-standards-deck" />
            <DeckRow file="Hale_ToxIntel_Marketplace_Deck.pptx"
                     name="HALE × ToxIntel Marketplace Deck"
                     meta="4 slides · Identical chrome to slides 1–7 · Platform-aligned pricing"
                     testid="download-marketplace-deck" />
            <DeckRow file="Hale_ToxIntel_Pitch_Pack.zip"
                     name="Pitch Pack · ZIP (both decks)"
                     meta="Recommended · ~315 KB"
                     testid="download-pitch-zip-row" />
          </div>

        </div>
      </div>
    </div>
  );
}


/* Single download row — fires a same-tab navigation to the `attachment`
 * URL via a programmatic anchor click. The browser intercepts the response
 * at the header stage (Content-Disposition: attachment) and triggers a
 * native download without ever navigating the iframe — which avoids the
 * mobile Chrome "blank new tab" gotcha that `target="_blank"` produces. */
function DeckRow({ file, name, meta, testid }) {
  const url = `${API_URL}/api/pitch/download/${file}`;

  const triggerDownload = () => {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = file; // hint; honored when same-origin, ignored otherwise
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Download started · check your Downloads folder');
    } catch (_) {
      // Last-resort fallback: open in a new tab so the browser still serves
      // the attachment from the response headers.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const copyUrl = async () => {
    const ok = await safeCopy(url);
    if (ok) toast.success('Link copied');
    else toast.info('Copy blocked — long-press the file name to copy manually');
  };

  return (
    <div className="flex items-center gap-2 mb-1.5 px-3 py-2 bg-[#0B0B0B] text-white rounded border border-[#E63946]/40">
      <FileText size={16} className="text-[#E63946] flex-shrink-0" weight="fill" />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-bold truncate">{name}</p>
        <p className="text-[9px] text-white/55 uppercase tracking-wider mt-0.5 truncate">{meta}</p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={triggerDownload}
          className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded bg-[#E63946] hover:bg-[#C9192C] text-black"
          data-testid={testid}>Download</button>
        <button onClick={copyUrl}
          className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white"
          data-testid={`${testid}-copy`}>Copy URL</button>
      </div>
    </div>
  );
}



/* Admin-only "rebuild pitch pack" — re-runs the python-pptx background
 * fix on every slide of the Standards deck and re-zips the pitch pack on
 * disk. Lets the operator iterate on deck content without an agent edit. */
function RebuildPitchPackButton({ onDone }) {
  const [busy, setBusy] = React.useState(false);

  const rebuild = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/pitch/rebuild`, {}, { withCredentials: true });
      const d = r.data || {};
      const kb = (n) => `${Math.round((n || 0) / 1024)} KB`;
      // Surface both background patches and price-sync writes so the operator
      // sees exactly what changed.
      const parts = [
        `${d.slides_patched}/${d.slides_total} slides patched`,
        `${d.prices_synced} prices synced`,
        `ZIP ${kb(d.zip_bytes)}`,
      ];
      toast.success(`Pitch pack rebuilt · ${parts.join(' · ')}`, { duration: 5000 });
      if (typeof onDone === 'function') onDone(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rebuild failed — admin only');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={rebuild} disabled={busy}
      className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] hover:text-[#111827] disabled:opacity-50 flex items-center gap-1"
      data-testid="rebuild-pitch-pack"
      title="Re-patch slide backgrounds + re-zip the pitch pack on disk">
      <Wrench size={11} weight="bold" />
      {busy ? 'Rebuilding…' : 'Rebuild pack'}
    </button>
  );
}
